import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@ngneat/transloco';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseHorizontalNavigationComponent,
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { EstablishmentSwitcherComponent } from 'app/layout/common/establishment-switcher/establishment-switcher.component';
import { Subject, takeUntil } from 'rxjs';
import { ImpersonationBannerComponent } from 'app/layout/common/impersonation-banner/impersonation-banner.component';
import { BottomNavigationComponent } from 'app/layout/common/bottom-navigation/bottom-navigation.component';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { TrialPeriodComponent } from 'app/layout/common/trial-period/trial-period.component';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';
import { Subscription } from 'app/modules/shared/models/subscription';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { PlanEnum } from 'app/modules/shared/enums/planEnum';
import { UrlUtils } from 'app/modules/shared/utils/url-utils';
import { PlanLimitTopBarComponent } from 'app/modules/shared/components/plan-limit-top-bar/plan-limit-top-bar.component';
import { PaymentFailedBannerComponent } from 'app/layout/common/payment-failed-banner/payment-failed-banner.component';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { TranslocoService } from '@ngneat/transloco';
import { UserUtil } from 'app/modules/shared/utils/user.util';
import { PlanService } from 'app/modules/partner/services/plan.service';
import { Plan } from 'app/modules/shared/models/plan';
import { PlanUtil } from 'app/modules/shared/utils/plan.util';
import { UpgradeService } from 'app/modules/shared/services/upgrade.service';

@Component({
    selector: 'modern-layout',
    templateUrl: './modern.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        FuseHorizontalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        NotificationsComponent,
        UserComponent,
        EstablishmentSwitcherComponent,
        RouterOutlet,
        QuickChatComponent,
        BottomNavigationComponent,
        TrialPeriodComponent,
        ImpersonationBannerComponent,
        PlanLimitTopBarComponent,
        PaymentFailedBannerComponent,
        TranslocoModule
    ],
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    isScreenMedium: boolean;
    navigation: Navigation;
    subscription: Subscription;
    user: User;
    private _plans: Plan[] = [];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _subscriptionService: SubscriptionService,
        private _userService: UserService,
        private _planService: PlanService,
        private _translocoService: TranslocoService,
        private _upgradeService: UpgradeService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Check if the upgrade button should be shown (non-PRO users who are not in trial)
     */
    get showUpgradeButton(): boolean {
        if (!this.subscription) return false;
        const isTrialPeriod = this.subscription.subscriptionStatusRefCode === 'TRL';
        if (isTrialPeriod) return false;
        // Resolve plan code: prefer direct field, fallback to planId lookup
        let resolvedPlanCode = this.subscription.planCode;
        if (!resolvedPlanCode && this.subscription.planId && this._plans.length) {
            const plan = PlanUtil.getPlanById(this.subscription.planId, this._plans);
            if (plan) {
                resolvedPlanCode = plan.planCode as string;
            }
        }
        return !!resolvedPlanCode && resolvedPlanCode !== PlanEnum.PRO;
    }

    /**
     * Getter to check if bottom navigation should be hidden
     * Hides bottom navigation when in optimized mode
     */
    get inOptimizedMode(): boolean {
        const result = UrlUtils.inOptimizedScreenMode(this._router.url, this.isScreenSmall);
        return result;
    }

    /**
     * Whether the current user is a partner user
     */
    get isPartnerUser(): boolean {
        return this.user ? UserUtil.isPartnerUser(this.user) : false;
    }

    /**
     * Getter for bottom navigation items
     */
    get bottomNavItems(): FuseNavigationItem[] {
        return [
            {
                id: 'menus',
                title: this._translocoService.translate('navigation.menu.title'),
                type: 'basic',
                icon: 'heroicons_outline:clipboard-document-list',
                link: `/${PathEnum.APP}/${PathEnum.APP_PARTNER_MENU}`,
                active: this._router.url.includes(`/${PathEnum.APP}/${PathEnum.APP_PARTNER_MENU}`)
            },
            {
                id: 'partner-dashboard',
                title: this._translocoService.translate('navigation.partner-dashboard.title'),
                type: 'basic',
                icon: 'heroicons_outline:chart-bar',
                link: `/${PathEnum.APP}/${PathEnum.APP_PARTNER_DASHBOARD}`,
                active: this._router.url.includes(`/${PathEnum.APP}/${PathEnum.APP_PARTNER_DASHBOARD}`)
            },
            {
                id: 'qr-code',
                title: this._translocoService.translate('navigation.qr-code.title'),
                type: 'basic',
                icon: 'heroicons_outline:qr-code',
                link: `/${PathEnum.APP}/${PathEnum.APP_PARTNER_QRCODE}`,
                active: this._router.url.includes(`/${PathEnum.APP}/${PathEnum.APP_PARTNER_QRCODE}`)
            },
            {
                id: 'establishment',
                title: this._translocoService.translate('navigation.establishment.title'),
                type: 'basic',
                icon: 'heroicons_outline:building-storefront',
                link: `/${PathEnum.APP}/${PathEnum.APP_PARTNER_ESTABLISHMENT}`,
                active: this._router.url.includes(`/${PathEnum.APP}/${PathEnum.APP_PARTNER_ESTABLISHMENT}`)
            }
        ];
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
                // Check if the screen is medium (md breakpoint)
                this.isScreenMedium = matchingAliases.includes('md') && !matchingAliases.includes('lg');
            });

        // Subscribe to plans data (for planCode lookup fallback)
        this._planService.allPlans$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(plans => {
                if (plans) {
                    this._plans = plans;
                }
            });

        // Subscribe to subscription changes
        this._subscriptionService.partnerSubscription$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((subscription: Subscription) => {
                this.subscription = subscription;
            });

        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Open the plan selection modal directly from the navbar
     */
    navigateToUpgrade(): void {
        this._upgradeService.openUpgradeModal();
    }

    /**
     * Navigate to home page when logo is clicked
     */
    onLogoClick(): void {
       
        this._navigationService.goToHomepage();

    }

}
